// @vendors
import React, { useState, useEffect } from 'react';

// @services
import { Company } from '../../services/semantic-search.service';
import { ServiceRequest } from '../../services/service.service';
import ServiceService from '../../services/service.service';
import { Message } from '../../services/openai.service';
import UserService from '../../services/user.service';
import { UserProfile } from '../../services/user.service';

// @components
import { getUserPhoneNumber, setUserApprovedCompany } from './AgentMessage';

// Estilos CSS personalizados para la barra de desplazamiento y animaciones
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  .dark .custom-scrollbar::-webkit-scrollbar-track {
    background: #2d3748;
  }
  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #4a5568;
  }
  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #718096;
  }

  @keyframes scroll-infinite {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }

  .animate-scroll-infinite {
    animation: scroll-infinite 40s linear infinite;
    display: flex;
    width: max-content;
  }

  .animate-scroll-infinite:hover {
    animation-play-state: paused;
  }
`;

interface CompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  company?: Company;
  onApprove?: (companyId: string, companyName: string) => void;
  conversationHistory?: Message[];
  serviceName?: string;
  categoryName?: string;
  cityName?: string;
}

const CompanyModal: React.FC<CompanyModalProps> = ({
  isOpen,
  onClose,
  company,
  onApprove,
  conversationHistory,
  serviceName,
  categoryName,
  cityName,
}) => {
  // Aplicar estilos de la barra de desplazamiento
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = scrollbarStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isCreatingService, setIsCreatingService] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  if (!company && !isOpen) return null;

  let companyImages: string[] = [];
  const serviceImages = (company as any)?.serviceImages || [];

  // Si hay serviceImages, las usamos como imágenes principales
  if (serviceImages && Array.isArray(serviceImages) && serviceImages.length > 0) {
    serviceImages.forEach((imgUrl: string) => {
      if (typeof imgUrl === 'string') {
        companyImages.push(imgUrl);
      }
    });
  }

  if (company?.profileImage && !companyImages.includes(company.profileImage)) {
    companyImages.unshift(company.profileImage);
  }

  if (companyImages.length === 0) {
    companyImages.push("https://via.placeholder.com/400x300?text=Imagen+Principal");
  }

  if (companyImages.length < 2) {
    const category = company?.category || "Servicio";
    companyImages.push(
      `https://via.placeholder.com/400x300?text=${category}+1`,
      `https://via.placeholder.com/400x300?text=${category}+2`
    );
  }

  // Datos de la empresa con fallbacks mínimos necesarios
  const companyData = {
    name: company ? `${company.name} ${company.lastName}` : "Empresa",
    logo: company?.profileImageUrl || company?.profileImage || "https://placehold.co/100x100/007BFF/FFFFFF?text=100x100",
    description: company?.companyDescription || "Sin descripción disponible",
    servicesCompleted: company ? Math.floor(Math.random() * 300) + 50 : 100, // Dato quemado solo si es necesario
    yearsExperience: company?.yearsExperience || 1,
    employeeCount: company?.employeeCount || 1,
    rating: company?.rating || 4.0,
    images: companyImages
  };

  // Control del carrusel de imágenes (muestra 3 a la vez)
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => {
      const maxIndex = companyData.images.length - 3;
      return prevIndex < maxIndex ? prevIndex + 1 : prevIndex;
    });
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : 0
    );
  };

  // Funciones para el modal de imagen ampliada
  const openImageModal = (index: number) => {
    setSelectedImageIndex(currentImageIndex + index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const nextImageInModal = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex < companyData.images.length - 1 ? prevIndex + 1 : 0
    );
  };

  const prevImageInModal = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : companyData.images.length - 1
    );
  };

  if (!isOpen) return null;

  // Función para manejar clics en el fondo del modal
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleApproveCompany = async () => {
    if (company && onApprove) {
      try {
        setIsCreatingService(true);
        setServiceError(null);
        const userProfile = await UserService.getCurrentUserProfile();

        let businessPrice = 0;
        if (userProfile?.business && typeof userProfile.business === 'number') {
          const leadPrice = Number(company.leadPrice) || 0;
          businessPrice = leadPrice * userProfile.business / 100;
        }
        await handleCreateEnhancedService(userProfile, businessPrice);
      } catch (error) {
        setServiceError('Error al crear el servicio. Por favor, intenta nuevamente.');
      } finally {
        setIsCreatingService(false);
      }
    }
  };

  // Crear la solicitud de servicio
  const handleCreateEnhancedService = async (userProfile: UserProfile, businessPrice: number) => {
    const userToken = localStorage.getItem('token');
    const capturedUserPhone = getUserPhoneNumber();
    const serviceRequest: ServiceRequest = {
      serviceName: serviceName || 'Servicio no especificado',
      userId: userProfile?.id,
      businessPrice: businessPrice,
      userName: userProfile ? `${userProfile.name} ${userProfile.lastName}` : undefined,
      userProfileImage: userProfile?.profileImage,
      serviceDate: new Date().toISOString(),
      categoryName: categoryName,
      companyName: `${company?.name} ${company?.lastName}`,
      companyId: company?.companyId,
      conversationHistory: conversationHistory,
      cityName: cityName || 'Medellín',
      userToken: userToken || undefined,
      leadPrice: company?.leadPrice || 0,
      UserPhone: capturedUserPhone || undefined
    };
    await ServiceService.createEnhancedService(serviceRequest);
    handleApprove();
  };

  const handleApprove = async () => {
    if (company && onApprove) {
      setUserApprovedCompany(true);
      onApprove(company.companyId, `${company.name} ${company.lastName}`);
      onClose();
    }
  };

  // Obtener servicios de la empresa
  const companyServices = (company as any)?.services || [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-60 overflow-y-auto"
      onClick={handleBackdropClick}
      style={{ display: 'flex' }}
    >
      <div
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-[98vw] sm:max-w-[95vw] lg:w-[70vw] max-h-[95vh] sm:max-h-[90vh] lg:max-h-[85vh] flex flex-col my-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {/* Botón de cerrar*/}
        <button
          onClick={onClose}
          className="lg:block absolute top-4 right-2 md:top-6 md:right-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 z-20 bg-white dark:bg-gray-700 rounded-full p-1 md:p-2 shadow-lg hover:shadow-xl transition-all"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header: Logo/Nombre y Calificaciones */}
        <div className="flex-col items-center justify-between p-4 sm:p-3 lg:p-4 lg:mr-16 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 flex-shrink-0">
          <div className="mb-4 flex items-center gap-4 sm:gap-2 lg:gap-4 flex-1 min-w-0">
            <img
              src={companyData.logo}
              alt={companyData.name}
              className="w-10 h-10 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full object-cover border-2 sm:border-4 border-white dark:border-gray-700 shadow-lg flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <h2 className="text-xl sm:text-base lg:text-xl font-bold text-gray-800 dark:text-white truncate">
                {companyData.name}
              </h2>
              <p className="text-[14px] sm:text-xs lg:text-sm text-gray-600 dark:text-gray-400 truncate">
                {company?.category || 'Servicio'}
              </p>
            </div>
          </div>
          
          {/* Calificaciones y botón cerrar en mobile */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <div className="flex items-center gap-0.5 sm:gap-1 lg:gap-2 bg-white dark:bg-gray-700 px-1.5 sm:px-2 lg:px-4 py-0.5 sm:py-1 lg:py-2 rounded-full shadow-md">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className={`w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6 ${i < Math.round(companyData.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <span className="text-xs sm:text-sm lg:text-lg font-bold text-gray-700 dark:text-gray-300">
                {companyData.rating.toFixed(1)}
              </span>
            </div>
            {/* Botón cerrar solo en mobile */}
           
          </div>
        </div>

        {/* Contenido Principal - Scroll en mobile, grid en desktop */}
        <div className="overflow-y-auto custom-scrollbar">
          <div className="lg:grid lg:grid-cols-2 lg:gap-6 p-2 sm:p-3 lg:p-4 space-y-3 sm:space-y-4 lg:space-y-0">
            {/* Historia */}
            <div className="flex flex-col">
              <h3 className="text-sm sm:text-md font-bold mb-2 sm:mb-3 text-gray-800 dark:text-white flex items-center gap-2 flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Quienes somos
              </h3>
              <div className="bg-white dark:bg-gray-900 rounded-lg sm:rounded-xl p-2 sm:p-3 max-h-48 sm:max-h-56 lg:max-h-72 overflow-y-auto custom-scrollbar shadow-md border border-gray-200 dark:border-gray-700">
                <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-relaxed">
                  {companyData.description}
                </p>
              </div>
            </div>

            {/* Servicios */}
            <div className="flex flex-col">
              <h3 className="text-sm sm:text-md font-bold mb-2 sm:mb-3 text-gray-800 dark:text-white flex items-center gap-2 flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Servicios Ofrecidos
              </h3>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-blue-900/30 rounded-lg sm:rounded-xl p-2 sm:p-3 max-h-52 sm:max-h-64 lg:max-h-60 overflow-y-auto custom-scrollbar shadow-inner">
                {companyServices.length > 0 ? (
                  <ul className="space-y-2">
                    {companyServices.map((service: string, index: number) => (
                      <li 
                        key={index}
                        className="flex items-center gap-2 sm:gap-3 bg-white dark:bg-gray-800 p-1.5 sm:p-2 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold">
                          {index + 1}
                        </div>
                        <span className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm truncate flex-1" title={service}>
                          {service}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    No hay servicios disponibles
                  </div>
                )}
              </div>
            </div>

            {/* Galería de Imágenes */}
            <div className="flex flex-col">
              <h3 className="text-sm sm:text-md font-bold mb-2 sm:mb-3 text-gray-800 dark:text-white flex items-center gap-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Galería de Trabajos
              </h3>
              
              <div className="relative rounded-lg sm:rounded-xl shadow-inner bg-white dark:bg-gray-800 h-28 sm:h-32 overflow-hidden">
                <div className="flex gap-3 h-full px-2 py-2">
                  {companyData.images.slice(currentImageIndex, currentImageIndex + 3).map((image, index) => (
                    <div 
                      key={index} 
                      className="flex-shrink-0 w-36 h-28 cursor-pointer"
                      onClick={() => openImageModal(index)}
                    >
                      <img
                        src={image}
                        alt={`Trabajo ${currentImageIndex + index + 1}`}
                        className="w-full h-full object-cover rounded-lg shadow-md hover:scale-105 hover:shadow-xl transition-all duration-300"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = `https://via.placeholder.com/400x300?text=Imagen+${currentImageIndex + index + 1}`;
                        }}
                      />
                    </div>
                  ))}
                </div>

                {companyData.images.length > 3 && (
                  <>
                    <button
                      onClick={prevImage}
                      disabled={currentImageIndex === 0}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 disabled:bg-black/30 text-white p-2 rounded-full transition-all shadow-lg disabled:cursor-not-allowed"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={nextImage}
                      disabled={currentImageIndex >= companyData.images.length - 3}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 disabled:bg-black/30 text-white p-2 rounded-full transition-all shadow-lg disabled:cursor-not-allowed"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                      {Array.from({ length: Math.max(1, companyData.images.length - 2) }).map((_, index) => (
                        <div
                          key={index}
                          className={`h-1.5 rounded-full transition-all ${
                            currentImageIndex === index ? 'w-6 bg-pink-600' : 'w-1.5 bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Cards de estadísticas y botón (solo visible en desktop) */}
            <div className="hidden lg:block space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 text-center shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-2xl font-bold text-white">
                    {companyData.servicesCompleted}
                  </div>
                  <div className="text-[10px] text-blue-100 mt-1 font-medium">
                    Servicios realizados
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-3 text-center shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-2xl font-bold text-white">
                    {companyData.yearsExperience}
                  </div>
                  <div className="text-[10px] text-green-100 mt-1 font-medium">
                    Años de experiencia
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-3 text-center shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-2xl font-bold text-white">
                    {companyData.employeeCount}
                  </div>
                  <div className="text-[10px] text-purple-100 mt-1 font-medium">
                    Empleados
                  </div>
                </div>
              </div>

              {/* Botón de aprobar - Desktop */}
              <div>
                {serviceError && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-3 text-sm">
                    {serviceError}
                  </div>
                )}

                <button
                  onClick={() => handleApproveCompany()}
                  disabled={isCreatingService}
                  className={`w-full ${isCreatingService ? 'bg-gray-400' : 'bg-xervix-primary from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600'} text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl flex justify-center items-center text-md`}
                >
                  {isCreatingService ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      PROCESANDO...
                    </>
                  ) : (
                    'APROBAR EMPRESA'
                  )}
                </button>
              </div>
            </div>

            {/* Cards de estadísticas (solo visible en mobile/tablet) */}
            <div className="lg:hidden grid grid-cols-3 gap-2 sm:gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-2 sm:p-3 text-center shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-lg sm:text-2xl font-bold text-white">
                  {companyData.servicesCompleted}
                </div>
                <div className="text-[9px] sm:text-[10px] text-blue-100 mt-0.5 sm:mt-1 font-medium">
                  Servicios realizados
                </div>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-2 sm:p-3 text-center shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-lg sm:text-2xl font-bold text-white">
                  {companyData.yearsExperience}
                </div>
                <div className="text-[9px] sm:text-[10px] text-green-100 mt-0.5 sm:mt-1 font-medium">
                  Años de experiencia
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-2 sm:p-3 text-center shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-lg sm:text-2xl font-bold text-white">
                  {companyData.employeeCount}
                </div>
                <div className="text-[9px] sm:text-[10px] text-purple-100 mt-0.5 sm:mt-1 font-medium">
                  Empleados
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botón de aprobar fijo en la parte inferior - Solo Mobile */}
        <div className="lg:hidden sticky bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-3 shadow-lg">
          {serviceError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded-lg mb-2 text-xs">
              {serviceError}
            </div>
          )}

          <button
            onClick={() => handleApproveCompany()}
            disabled={isCreatingService}
            className={`w-full ${isCreatingService ? 'bg-gray-400' : 'bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600'} text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl flex justify-center items-center text-sm sm:text-md`}
          >
            {isCreatingService ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                PROCESANDO...
              </>
            ) : (
              'APROBAR EMPRESA'
            )}
          </button>
        </div>
      </div>

      {/* Modal de Imagen Ampliada */}
      {isImageModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90"
          onClick={closeImageModal}
        >
          <div className="relative w-full h-full flex items-center justify-center p-8">
            {/* Botón de cerrar */}
            <button
              onClick={closeImageModal}
              className="absolute top-6 right-6 text-white hover:text-gray-300 z-10 bg-black/50 rounded-full p-3 hover:bg-black/70 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Imagen ampliada */}
            <div 
              className="relative max-w-5xl max-h-[85vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={companyData.images[selectedImageIndex]}
                alt={`Trabajo ${selectedImageIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = `https://via.placeholder.com/800x600?text=Imagen+${selectedImageIndex + 1}`;
                }}
              />

              {/* Botones de navegación */}
              {companyData.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImageInModal();
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-4 rounded-full transition-all shadow-lg"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImageInModal();
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-4 rounded-full transition-all shadow-lg"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Contador de imágenes */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                {selectedImageIndex + 1} / {companyData.images.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyModal;
